// What the staffer is looking at right now.
//
// The ~ console (components/Console.jsx) is global and has no idea which page
// is mounted, so "can you tell me what's meaningful inside the account I'm in?"
// used to get "I don't have your session context". Pages publish their slice of
// context here with useViewContext(); the console reads the merged view and
// sends it to the agent alongside the conversation.
//
// Entries are a stack keyed by `level` so nested views layer instead of
// replacing: level 0 is App's page chrome, level 1 a record (account, case),
// level 2 anything deeper. Effects in React fire child-before-parent, so the
// explicit level — not push order — is what orders the merge.

import { useEffect, useRef } from 'react';

const stack = [];              // [{ id, level, ctx }]
const subscribers = new Set();
let nextId = 1;

function ordered() {
  // Stable sort: general (low level) first so specific entries win the merge.
  return stack
    .map((e, i) => ({ e, i }))
    .sort((a, b) => (a.e.level - b.e.level) || (a.i - b.i))
    .map(({ e }) => e);
}

// Flat merge of every mounted entry + a breadcrumb of their labels, e.g.
// { breadcrumb: ['Customer Lookup', 'Acme Corp'], page: 'search', accountId: '001...' }
export function getViewContext() {
  if (stack.length === 0) return null;
  const merged = {};
  const breadcrumb = [];
  for (const { ctx } of ordered()) {
    const { label, ...rest } = ctx;
    if (label) breadcrumb.push(label);
    Object.assign(merged, rest);
  }
  return { breadcrumb, ...merged };
}

export function subscribeViewContext(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function notify() {
  const snapshot = getViewContext();
  for (const fn of subscribers) fn(snapshot);
}

// Publish this component's context for as long as it stays mounted. Pass null
// (or undefined) while data is still loading — nothing is published until
// there's something real to say.
export function useViewContext(ctx, level = 1) {
  const idRef = useRef(0);
  if (idRef.current === 0) idRef.current = nextId++;

  // Serialize so callers can pass a fresh object literal every render without
  // re-publishing (and re-rendering the console) on every render.
  const key = JSON.stringify(ctx ?? null);

  useEffect(() => {
    const id = idRef.current;
    const idx = stack.findIndex(e => e.id === id);
    const value = key === 'null' ? null : JSON.parse(key);
    if (!value) {
      if (idx !== -1) { stack.splice(idx, 1); notify(); }
      return;
    }
    if (idx === -1) stack.push({ id, level, ctx: value });
    else stack[idx] = { id, level, ctx: value };
    notify();
  }, [key, level]);

  // Unmount cleanup lives in its own effect so it runs at teardown only, not
  // on every context change.
  useEffect(() => () => {
    const idx = stack.findIndex(e => e.id === idRef.current);
    if (idx !== -1) { stack.splice(idx, 1); notify(); }
  }, []);
}
